/**
 * Admin: one member's profile. Route: /admin/members/:memberId (Admin role or Member.Read).
 * Keyed by MemberID so visitors and children without a login open too. Everything lives in the
 * shared MemberProfile component in admin mode.
 */
import { useCallback, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { IconArrowLeft, IconExternalLink, IconRefresh } from '@tabler/icons-react';
import { LayoutContainer } from '@/components/ui/layout';
import { Stack, Group, Button, Alert } from '@/components/ui';
import { FadeIn } from '@/components/animations';
import { usePermissions } from '@/components/ccms-components/auth/hooks/usePermissions';
import { PageHelpButton } from '@/components/help';
import { MemberProfile, memberFullName, type MemberRecord } from '@/components/members';
// Call campaigns: the Journey card (DA, E1 to E5). Hides itself when the user lacks access or the call fails.
import { MemberJourneyCard } from '@/pages/app/CallCentreManagement/components/MemberJourneyCard';

/** In-app help page key (helpRegistry.ts, MEMBERS_PAGES): the page's one "?" and the profile's tab help. */
const HELP_PAGE = 'members.member';

export default function MemberDetailPage() {
  const { memberId } = useParams<{ memberId: string }>();
  const navigate = useNavigate();
  const { hasRole } = usePermissions();
  const queryClient = useQueryClient();
  const id = Number(memberId);
  const valid = Number.isInteger(id) && id > 0;
  const [member, setMember] = useState<MemberRecord | null>(null);
  const onMemberLoaded = useCallback((m: MemberRecord | null) => setMember(m), []);

  return (
    <LayoutContainer>
      <FadeIn>
        <Stack gap="md">
          <Group justify="space-between" wrap="wrap">
            <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={() => navigate('/admin/members')}>
              Back to members
            </Button>
            <Group gap="xs">
              {member?.userId && hasRole('Admin') && (
                <Button variant="light" size="sm" leftSection={<IconExternalLink size={16} />} onClick={() => navigate(`/admin/users/${member.userId}`)}>
                  Open the login
                </Button>
              )}
              <PageHelpButton pageKey={HELP_PAGE} />
            </Group>
          </Group>

          {!valid ? (
            <Alert color="yellow" title="Member not found">The link does not contain a valid member ID.</Alert>
          ) : (
            <>
              <MemberProfile
                key={id}
                mode="admin"
                memberId={id}
                onMemberLoaded={onMemberLoaded}
                helpPageKey={HELP_PAGE}
                headerActions={
                  <Button size="xs" variant="subtle" leftSection={<IconRefresh size={14} />} onClick={() => queryClient.invalidateQueries({ queryKey: ['members', 'profile', id] })}>
                    Refresh
                  </Button>
                }
              />
              <MemberJourneyCard key={`journey-${id}`} memberId={id} memberName={member ? memberFullName(member) || null : null} />
            </>
          )}
        </Stack>
      </FadeIn>
    </LayoutContainer>
  );
}
